import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { BUNDLED_WORKSHOPS } from "../../data/bundledWorkshops";

export default function CmsList() {
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const teacher = JSON.parse(localStorage.getItem("dm_teacher") || "{}");
  const isAdmin = teacher?.role === "admin";

  const allTags = Array.from(
    new Set(workshops.flatMap((w) => w.tags || []))
  ).sort();

  const filtered = workshops.filter((w) => {
    if (tierFilter !== "all" && !(w.tiers || []).includes(tierFilter)) return false;
    if (tagFilter !== "all" && !(w.tags || []).includes(tagFilter)) return false;
    if (search) {
      const q = search.toLowerCase();
      const haystack = `${w.title} ${w.subtitle || ""} ${w.description || ""} ${(w.tags || []).join(" ")}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const load = async () => {
    try {
      const { data } = await api.get("/workshops");
      setWorkshops(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/workshops/${id}/duplicate`);
      toast.success("Workshop duplikován");
      navigate(`/teacher/cms/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chyba při duplikaci");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Opravdu smazat tento workshop?")) return;
    try {
      await api.delete(`/workshops/${id}`);
      toast.success("Smazáno");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nelze smazat");
    }
  };

  const createNew = async () => {
    const title = window.prompt("Název nového workshopu:", "Nový workshop");
    if (!title) return;
    try {
      const { data } = await api.post("/workshops", { title, challenges: [] });
      navigate(`/teacher/cms/${data.id}`);
    } catch (e) {
      toast.error("Nepodařilo se vytvořit");
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch (err) {
        toast.error("Neplatný JSON: " + err.message);
        return;
      }
      // Accept either the {schema_version, workshop} wrapper or a bare workshop object.
      if (!payload.schema_version && payload.title && payload.topics) {
        payload = { schema_version: "1.0", workshop: payload };
      }
      const { data } = await api.post("/workshops/import", payload);
      toast.success(`Workshop ${data.action === "created" ? "importován" : "aktualizován"} — ${data.topic_count} témat, ${data.question_count} otázek`);
      await load();
      navigate(`/teacher/cms/${data.workshop_id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.errors)) {
        toast.error("Chyby validace:\n" + detail.errors.slice(0, 4).join("\n"));
      } else {
        toast.error(detail || "Import selhal");
      }
    } finally {
      setImporting(false);
    }
  };

  const [addingBundled, setAddingBundled] = useState(null);
  const [bundledOpen, setBundledOpen] = useState(false);
  const addBundled = async (item) => {
    if (!window.confirm(`Přidat workshop „${item.label}" do databáze?\n\nPozor: pokud už workshop se stejným ID existuje, jeho obsah se přepíše tímto vzorem (včetně případných úprav v editoru).`)) return;
    setAddingBundled(item.key);
    try {
      const { data } = await api.post("/workshops/import", item.payload);
      toast.success(`„${item.label}" ${data.action === "created" ? "přidán" : "aktualizován"} — ${data.topic_count} témat, ${data.question_count} otázek`);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Přidání selhalo");
    } finally {
      setAddingBundled(null);
    }
  };

  const downloadSchema = async () => {
    try {
      const { data } = await api.get("/workshops/schema");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "workshop-schema-v1.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Nepodařilo se stáhnout schéma");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/teacher" className="text-slate-500 hover:text-slate-900 text-sm font-bold">← Session</Link>
            <div className="font-heading font-black text-lg tracking-tight text-slate-900">Správa obsahu</div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <button
                  onClick={downloadSchema}
                  className="text-slate-600 hover:text-slate-900 text-sm font-bold border border-slate-200 rounded-md px-3 py-2"
                  data-testid="btn-schema"
                  title="Stáhnout JSON Schema pro validaci ve tvém editoru"
                >
                  📐 Schéma
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold rounded-md px-4 py-2 text-sm"
                  data-testid="btn-import-workshop"
                >
                  {importing ? "Importuji…" : "⤒ Import JSON"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={handleImportFile}
                  data-testid="input-import-file"
                />
                {BUNDLED_WORKSHOPS.length > 0 && (
                  <div className="relative">
                    <button
                      onClick={() => setBundledOpen((o) => !o)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-md px-4 py-2 text-sm"
                      data-testid="btn-bundled-menu"
                    >
                      ➕ Přidat workshop ▾
                    </button>
                    {bundledOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setBundledOpen(false)} />
                        <div className="absolute right-0 mt-1 w-60 bg-white border border-slate-200 rounded-md shadow-lg z-20 py-1" data-testid="bundled-menu">
                          <div className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-slate-400">Připravené workshopy</div>
                          {BUNDLED_WORKSHOPS.map((item) => (
                            <button
                              key={item.key}
                              onClick={() => { setBundledOpen(false); addBundled(item); }}
                              disabled={addingBundled === item.key}
                              className="w-full text-left px-3 py-2 text-sm font-bold text-slate-700 hover:bg-emerald-50 disabled:opacity-50"
                              data-testid={`btn-add-bundled-${item.key}`}
                            >
                              {addingBundled === item.key ? "Přidávám…" : item.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
            <button
              onClick={createNew}
              disabled={!isAdmin}
              title={!isAdmin ? "Pouze master admin může vytvářet workshopy" : ""}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold rounded-md px-4 py-2 text-sm"
              data-testid="btn-new-workshop"
            >
              + Nový workshop
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-heading font-black tracking-tighter text-slate-900">Knihovna workshopů</h1>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin
              ? "Systémové šablony jsou chráněné — duplikuj si je pro úpravu."
              : "Jako lektor můžeš workshopy prohlížet. Úpravy a vlastní workshopy spravuje master admin."}
          </p>
        </div>

        <div className="mb-4 bg-white border border-slate-200 rounded-lg p-4 grid gap-3 md:grid-cols-[1fr_180px_180px_auto] items-end">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Hledat</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Název, popis, tag…"
              className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
              data-testid="cms-search"
            />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Stupeň</label>
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
              data-testid="cms-tier-filter"
            >
              <option value="all">Všechny</option>
              <option value="mladsi_zs">3.–5. ZŠ</option>
              <option value="starsi_zs">6.–9. ZŠ</option>
              <option value="stredni">SŠ</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Téma (tag)</label>
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
              data-testid="cms-tag-filter"
            >
              <option value="all">Všechna</option>
              {allTags.map((t) => (
                <option key={t} value={t}>#{t}</option>
              ))}
            </select>
          </div>
          <div className="text-xs text-slate-500 whitespace-nowrap" data-testid="cms-count">
            {filtered.length} / {workshops.length}
          </div>
        </div>

        {loading ? (
          <div className="text-slate-500">Načítám…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-500">
            Tomu hledání nic neodpovídá. Zkus jiný stupeň nebo téma.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((w) => (
              <div key={w.id} className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${w.is_system ? "bg-slate-900 text-white" : "bg-green-100 text-green-800"}`}>
                    {w.is_system ? "Systémový" : "Vlastní"}
                  </span>
                  <span className="text-xs text-slate-400">{w.challenge_count} výzev</span>
                </div>
                <div>
                  <div className="font-heading font-black text-lg tracking-tight text-slate-900 leading-tight">{w.title}</div>
                  <div className="text-sm text-slate-500 mt-1 line-clamp-2">{w.subtitle || w.description}</div>
                </div>
                <div className="flex flex-wrap gap-1 text-[10px]">
                  {(w.tags || []).slice(0, 4).map((t) => (
                    <span key={t} className="bg-slate-100 px-2 py-0.5 rounded-full text-slate-600">#{t}</span>
                  ))}
                </div>
                <div className="mt-auto flex gap-2 pt-2">
                  <Link
                    to={`/teacher/workshop-preview/${w.id}`}
                    className="flex-1 text-center bg-amber-100 hover:bg-amber-200 text-amber-900 font-bold rounded-md px-3 py-2 text-sm border border-amber-300"
                    data-testid={`btn-preview-${w.id}`}
                  >
                    📚 Příprava
                  </Link>
                  <Link
                    to={`/teacher/cms/${w.id}`}
                    className="flex-1 text-center bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-md px-3 py-2 text-sm"
                    data-testid={`btn-edit-${w.id}`}
                  >
                    {(!isAdmin || w.is_system) ? "Zobrazit" : "Upravit"}
                  </Link>
                  {isAdmin && (
                    <button
                      onClick={() => duplicate(w.id)}
                      className="bg-white border border-slate-200 hover:border-slate-900 text-slate-900 font-bold rounded-md px-3 py-2 text-sm"
                      data-testid={`btn-duplicate-${w.id}`}
                    >
                      Dup
                    </button>
                  )}
                  {isAdmin && !w.is_system && (
                    <button
                      onClick={() => remove(w.id)}
                      className="bg-white border border-red-200 hover:border-red-600 text-red-600 font-bold rounded-md px-3 py-2 text-sm"
                      data-testid={`btn-delete-${w.id}`}
                    >
                      🗑
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
